import { useQuery } from 'react-query';
import { groupsApi } from '../../../services/groups-api-client';
import { probeAudienceKindSupport } from '../../../lib/audienceKind';
import { RESOLVED_AUDIENCES } from './audience';

/**
 * Does the API decide RSVP audiences at send time (OrchestratorAPI #394)?
 *
 * One dry-run probe per group per session — see `probeAudienceKindSupport` for
 * why "assume yes and handle the 400" is not safe (an older API IGNORES the
 * field and would send to the whole roster). `supported` is false until the
 * probe has proved otherwise, so a composer that mounts before the answer
 * arrives sends today's frozen ids, never an unproven `audienceKind`.
 *
 * `audience` scopes the probe to the moment it matters: pass the composer's
 * audience and nothing is probed until an RSVP one is chosen; omit it (the plan
 * dialog) to probe immediately. Both callers share the one cache entry.
 */
export function useAudienceKindSupport(groupId: string, audience?: string): { supported: boolean; checked: boolean } {
  const needed = audience === undefined || RESOLVED_AUDIENCES.has(audience);
  const query = useQuery(
    ['audience-kind-support', groupId],
    () => probeAudienceKindSupport((request) => groupsApi.sendBroadcast(groupId, request), { throwOnTransient: true }),
    {
      enabled: needed,
      staleTime: Infinity,
      cacheTime: Infinity,
      // Only a DEFINITIVE answer is cached for the session. A network error, a
      // 401 or a 5xx is retried (and re-polled) instead of freezing "unsupported".
      retry: 2,
      refetchInterval: (data) => (data === undefined ? 15_000 : false),
      refetchOnWindowFocus: false,
    },
  );
  return { supported: query.data === true, checked: query.isSuccess };
}
